import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import "dayjs/locale/el";
import BookedSpeakerCard from "../../../components/BookedSpeakerCard/BookedSpeakerCard";
import {
  PencilSquareIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Modal from "../../../components/Modal/Modal";
import {
  doc,
  addDoc,
  collection,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { toast } from "react-toastify";
import FiltersButton from "../../../components/FiltersButton/FiltersButton";
import Filters from "../../../components/Filters/Filters";
import * as XLSX from "xlsx";
import { titles } from "../../../Titles";
import { useUser } from "../../../context/UserContext";
import AddNewEventForModalForm from "../../../components/AddNewEventForUser/AddNewEventForModalForm";
import AddNewEventForUserButton from "../../../components/AddNewEventForUser/AddNewEventForUserButton";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useRealtimeSpeakers } from "../../../hooks/useRealtimeData";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const AdminDashboard = () => {
  const [sundaysByMonth, setSundaysByMonth] = useState([]);
  const [open, setOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [bookedDate, setBookedDate] = useState("bookedDate");
  const { selectedYear, setSelectedYear, user } = useUser();
  const [openNewEvent, setOpenNewEvent] = useState(false);
  const [inputs, setInputs] = useState({
    speakerName: "",
    meetingHouse: "",
    number: "",
  });
  
  // Ref for current month card to scroll to it
  const currentMonthRef = useRef(null);

  // Use realtime hook instead of manual fetching
  const { speakers: bookedData, loading, error } = useRealtimeSpeakers(selectedYear);

  useEffect(() => {
    setSelectedYear(selectedYear);
    fetchSundays(selectedYear);
  }, [selectedYear, setSelectedYear]);

  // Scroll to current month when data is loaded
  useEffect(() => {
    if (sundaysByMonth.length > 0 && !loading && currentMonthRef.current) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const element = currentMonthRef?.current;
        const elementTop = element?.offsetTop;
        const offset = 50; // Add gap from top to prevent header being hidden
        
        window.scrollTo({
          top: elementTop - offset,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [sundaysByMonth, loading]);



  const createSpeakerBooking = async (data) => {
    const id = toast.loading("Δημιουργία...");
    try {
      const year = dayjs(data?.bookedDate).format("YYYY");
      await addDoc(collection(db, "speakers"), {
        ...data,
        year,
      });
      setOpen(false);
      setBookedDate("");
      toast.update(id, {
        render: "Έκλεισε με επιτυχία",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });
    } catch (e) {
      toast.update(id, {
        render: e,
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  const editSpeakerBooking = async (data) => {
    const id = toast.loading("Επεξεργασία...");
    try {
      await updateDoc(doc(db, "speakers", data?.id), data);
      setOpen(false);
      setBookedDate("");
      toast.update(id, {
        render: "Άλλαξε με επιτυχία",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });
    } catch (e) {
      toast.update(id, {
        render: e,
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  const deleteSpeakerBooking = async (data) => {
    const id = toast.loading("Διαγραφή...");
    try {
      if (
        window.confirm(
          `Είσαι σίγουρος ότι θέλεις να διαγράψεις τον ${data?.speakerName} για την ημερομηνία ${data?.bookedDate} `,
        )
      ) {
        await deleteDoc(doc(db, "speakers", data?.id));
        setOpen(false);
        setBookedDate("");
        toast.update(id, {
          render: "Διαγράφθηκε με επιτυχία",
          type: "success",
          isLoading: false,
          autoClose: 1000,
        });
      }
    } catch (e) {
      toast.update(id, {
        render: e,
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  const fetchSundays = (year) => {
    const sundaysArray = [];
    const startDate = dayjs().year(year).startOf("year");
    const endDate = dayjs().year(year).endOf("year");
    dayjs.locale("el"); // Set locale to Greek

    let currentMonth = startDate.month();
    let sundaysOfMonth = [];

    for (
      let currentDate = startDate;
      currentDate.isBefore(endDate);
      currentDate = currentDate.add(1, "day")
    ) {
      if (currentDate.day() === 0) {
        sundaysOfMonth.push({
          display: currentDate.format("DD-MM"),
          value: currentDate.format("YYYY-MM-DD"),
        });
      }
      if (
        currentDate.month() !== currentMonth ||
        currentDate.isSame(endDate, "day")
      ) {
        sundaysArray.push({
          month: currentDate.subtract(1, "day").format("MMMM"),
          year: currentDate.subtract(0, "year").format("YYYY"),
          monthNum: currentDate.subtract(1, "day").format("MM"),
          sundays: sundaysOfMonth,
        });
        sundaysOfMonth = [];
        currentMonth = currentDate.month();
      }
    }

    setSundaysByMonth(sundaysArray);
  };

  const exportToExcel = (monthText, month, year) => {
    const data = [["Ημερομηνία", "Ομιλητής", "Θέμα", "Εκκλησία"]];
    const printedMonth = bookedData.filter((item) => {
      return (
        dayjs(item?.bookedDate).format("MM") === month &&
        dayjs(item?.bookedDate).format("YYYY") === year
      );
    });

    printedMonth.forEach((item) => {
      data.push([
        dayjs(item?.bookedDate).format("DD-MM-YYYY"),
        item?.speakerName,
        titles.find((title) => title.number === item.number)?.name || "-",
        item.meetingHouse,
      ]);
    });

    const wb = XLSX.utils.book_new();
    const wscols = [
      { wch: 15 }, // width of column A
      { wch: 20 }, // width of column B
      { wch: 50 }, // width of column C
      { wch: 20 }, // width of column D
    ];
    const wsrows = [
      { hch: 10 }, // width of column A
      { hch: 6 }, // width of column B
      { hch: 6 }, // width of column C
      { hch: 6 }, // width of column D
      { hch: 6 }, // width of column D
      { hch: 6 }, // width of column D
      { hch: 6 }, // width of column D
    ];
    // Add a worksheet with the data
    const ws = XLSX.utils.aoa_to_sheet(data);
    // Set column width in the worksheet
    ws["!cols"] = wscols;
    ws["!rows"] = wsrows;
    XLSX.utils.book_append_sheet(wb, ws, monthText);

    // Save the workbook as an Excel file
    XLSX.writeFile(wb, `Πρόγραμμα ομιλιών ${monthText} ${year}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Φόρτωση...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-red-500">Σφάλμα: {error}</div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={() => Promise.resolve()}>
      <div className={"pb-28"}>
        <div className={"mx-auto max-w-7xl px-2 sm:px-6 lg:px-8"}>
          {sundaysByMonth.map((month, index) => {
            const isCurrentMonth = dayjs().format("MM") === month?.monthNum && 
                                  dayjs().format("YYYY") === month?.year;
            
            return (
              <div
                key={index}
                ref={isCurrentMonth ? currentMonthRef : null}
                className={"border-[1px] m-2 rounded-xl overflow-hidden"}
              >
                <div
                  className={classNames(
                    isCurrentMonth
                      ? "bg-emerald-700"
                      : "bg-gray-800",
                    "border-b-[1px] p-2 flex justify-between text-white",
                  )}
              >
                {month.month} - {month.year}
                <button
                  onClick={() =>
                    exportToExcel(month.month, month.monthNum, month.year)
                  }
                >
                  <PrinterIcon className={"w-5 h-6"} />{" "}
                </button>
              </div>
              <ul>
                {month.sundays.map((sunday, sundayIndex) => (
                  <div
                    key={sundayIndex}
                    className={
                      "flex flex-row h-full justify-between items-center border-b-[1px] last:border-0"
                    }
                  >
                    <div
                      className={
                        "flex flex-row w-full h-full items-center gap-3"
                      }
                    >
                      <li
                        className={
                          "w-16 h-24 p-2 flex justify-center items-center bg-gray-300 shrink-0"
                        }
                      >
                        {sunday.display}
                      </li>
                      {bookedData?.map((item, index) => {
                        if (item?.bookedDate === sunday?.value) {
                          return (
                            <BookedSpeakerCard key={index} bookedData={item} />
                          );
                        }
                        return null;
                      })}
                    </div>
                    {user?.role === "admin" && (bookedData?.find(
                      (item) => item?.bookedDate === sunday?.value,
                    ) ? (
                      <div className={"grid gap-3 px-2"}>
                        <button
                          className={"hover:bg-gray-100 rounded-xl p-2"}
                          onClick={() => {
                            setBookedDate(sunday?.value);
                            setOpen(true);
                            setInputs(
                              bookedData?.find(
                                (item) => item?.bookedDate === sunday?.value,
                              ),
                            );
                          }}
                        >
                          <PencilSquareIcon className={"w-4 h-4"} />
                        </button>
                        <button
                          className={"hover:bg-red-100 rounded-xl p-2"}
                          onClick={() => {
                            deleteSpeakerBooking(
                              bookedData?.find(
                                (item) => item?.bookedDate === sunday?.value,
                              ),
                            );
                          }}
                        >
                          <TrashIcon className={"w-4 h-4 text-red-500"} />
                        </button>
                      </div>
                    ) : (
                      <button
                        className={"hover:bg-green-100 rounded-xl p-2 mx-2"}
                        onClick={() => {
                          setBookedDate(sunday?.value);
                          setOpen(true);
                          setInputs({
                            speakerName: "",
                            meetingHouse: "",
                            number: "",
                          });
                        }}
                      >
                        <PlusIcon className={"w-4 h-4"} />
                      </button>
                    ))}
                  </div>
                ))}
              </ul>
              </div>
            );
          })}
        </div>
{          user?.role === "admin" && (
        <FiltersButton setOpen={setFiltersOpen} />
    )}

        {user?.role === "admin" && (
        <AddNewEventForUserButton setOpen={setOpenNewEvent} />
        )}
        <Modal
          open={open}
          setOpen={setOpen}
          bookedDate={bookedDate}
          createSpeakerBooking={createSpeakerBooking}
          editSpeakerBooking={editSpeakerBooking}
          inputs={inputs}
          setInputs={setInputs}
        />
        
        <Filters
          bookedData={bookedData}
          open={filtersOpen}
          setOpen={setFiltersOpen}
          />

        <AddNewEventForModalForm
          open={openNewEvent}
          setOpen={setOpenNewEvent}
        />

      </div>
    </PullToRefresh>
  );
};

export default AdminDashboard;
